/**
 * Japanese-calendar helpers for the scheduler timeline header.
 *
 * - `localizedWeekday(date, lang)` — locale-aware short weekday. JA gives
 *   single-char kanji (月火水木金土日); EN gives 3-letter abbreviations
 *   (Mon/Tue/…). Backed by Luxon for consistency with `@utils/dateUtils`.
 * - `localizedDayNumber(date, lang)` — day-of-month formatted via
 *   `Intl.NumberFormat` with the active locale (full-width digits for JA
 *   so the calendar reads as Japanese-styled).
 * - `rokuyo(date)` — 六曜 (Senshō/Tomobiki/…). Computed from the lunar
 *   (month, day) via `(m + d - 2) mod 6`. Luxon doesn't expose the chinese
 *   calendar, so we use a native `Intl.DateTimeFormat` for that single
 *   conversion. JP 旧暦 and Chinese lunar share the month/day for rokuyō.
 * - `localizedMonthYear` / `localizedMonth` / `localizedQuarterLabel` — the
 *   period and tick labels of the timeline header, in the active language.
 * - `isSaturday` / `isSunday` / `isWeekend` — predicates used to color
 *   weekend columns in the header (text) and in the gantt body (column
 *   overlays).
 *
 * Rokuyō: the canonical cycle starts at 先勝 on lunar 1/1, so the lookup
 * table is ordered 0=先勝, 1=友引, 2=先負, 3=仏滅, 4=大安, 5=赤口. That
 * way `(month + day − 2) mod 6` on lunar (1, 1) resolves to 先勝, and the
 * jump at lunar-month boundaries falls out for free (e.g. lunar 12/1 →
 * (12+1−2)%6 = 5 = 赤口, matching published rokuyō tables).
 *
 * If `Intl.DateTimeFormat` doesn't support the chinese calendar in the
 * runtime, rokuyō returns `null` and the UI omits it.
 */

import { DateTime } from 'luxon';

const ROKUYO_NAMES = ['先勝', '友引', '先負', '仏滅', '大安', '赤口'] as const;
export type Rokuyo = (typeof ROKUYO_NAMES)[number];

export type CalendarLang = 'en' | 'ja';

/**
 * Locale-aware short weekday.
 * - EN → "Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun" (3 chars)
 * - JA → "月", "火", "水", "木", "金", "土", "日"        (1 char)
 *
 * Uses Luxon's `ccc` token (CLDR "short" weekday) — JA's CLDR short form is
 * already single-character, so we don't need a separate "narrow" path.
 */
export function localizedWeekday(date: Date, lang: CalendarLang): string {
  return DateTime.fromJSDate(date).setLocale(lang).toFormat('ccc');
}

// Full-width digits ("１２３" rather than "123") give the JA timeline a
// visibly Japanese-styled day-number row. Constructed once per language.
const DAY_NUMBER_FORMATTER: Record<CalendarLang, Intl.NumberFormat> = {
  en: new Intl.NumberFormat('en-US'),
  ja: new Intl.NumberFormat('ja-JP-u-nu-fullwide'),
};

/** Day-of-month formatted for the active language. */
export function localizedDayNumber(date: Date, lang: CalendarLang): string {
  return DAY_NUMBER_FORMATTER[lang].format(date.getDate());
}

/**
 * ISO-8601 week-of-year label for the Week-zoom header.
 * - EN → "W23"
 * - JA → "第23週"
 *
 * Luxon's `weekNumber` is the ISO week (Monday-start, 1–53), which matches the
 * timeline's `weekStartsOn: 1` week boundaries.
 */
export function localizedWeekLabel(date: Date, lang: CalendarLang): string {
  const week = DateTime.fromJSDate(date).weekNumber;
  return lang === 'ja' ? `第${week}週` : `W${week}`;
}

// Month / year labels for the timeline's period and tick rows. `Intl` gives
// the locale's own shape ("Oct 2026" / "2026年10月") — date-fns `format`
// without a locale would leave the JA header in English. The header dates are
// tz-naive local midnights, so no `timeZone` is set. Built once per language.
const MONTH_YEAR_FORMATTER: Record<CalendarLang, Intl.DateTimeFormat> = {
  en: new Intl.DateTimeFormat('en', { month: 'short', year: 'numeric' }),
  ja: new Intl.DateTimeFormat('ja', { month: 'short', year: 'numeric' }),
};
const MONTH_FORMATTER: Record<CalendarLang, Intl.DateTimeFormat> = {
  en: new Intl.DateTimeFormat('en', { month: 'short' }),
  ja: new Intl.DateTimeFormat('ja', { month: 'short' }),
};
const YEAR_FORMATTER: Record<CalendarLang, Intl.DateTimeFormat> = {
  en: new Intl.DateTimeFormat('en', { year: 'numeric' }),
  ja: new Intl.DateTimeFormat('ja', { year: 'numeric' }),
};

/** "Oct 2026" (EN) / "2026年10月" (JA). */
export function localizedMonthYear(date: Date, lang: CalendarLang): string {
  return MONTH_YEAR_FORMATTER[lang].format(date);
}

/** "Oct" (EN) / "10月" (JA). */
export function localizedMonth(date: Date, lang: CalendarLang): string {
  return MONTH_FORMATTER[lang].format(date);
}

/**
 * Calendar-quarter label for the Month-zoom period row. `Intl` has no quarter
 * field, so the shape is spelled per language (as `localizedWeekLabel` does):
 * - EN → "Q4 2026"
 * - JA → "2026年 第4四半期"
 */
export function localizedQuarterLabel(date: Date, lang: CalendarLang): string {
  const quarter = Math.floor(date.getMonth() / 3) + 1;
  const year = YEAR_FORMATTER[lang].format(date);
  return lang === 'ja' ? `${year} 第${quarter}四半期` : `Q${quarter} ${year}`;
}

export function isSaturday(date: Date): boolean {
  // Luxon weekday is 1=Mon … 7=Sun, so Saturday is 6.
  return DateTime.fromJSDate(date).weekday === 6;
}

export function isSunday(date: Date): boolean {
  return DateTime.fromJSDate(date).weekday === 7;
}

export function isWeekend(date: Date): boolean {
  const wd = DateTime.fromJSDate(date).weekday;
  return wd === 6 || wd === 7;
}

// Lazy-construct because some environments throw when the calendar is
// unsupported. We isolate the throw to one place and cache success/failure.
let lunarFormatter: Intl.DateTimeFormat | null | undefined;
function getLunarFormatter(): Intl.DateTimeFormat | null {
  if (lunarFormatter !== undefined) return lunarFormatter;
  try {
    lunarFormatter = new Intl.DateTimeFormat('en-u-ca-chinese', {
      month: 'numeric',
      day: 'numeric',
    });
  } catch {
    lunarFormatter = null;
  }
  return lunarFormatter;
}

// Memoised per day: `Intl.DateTimeFormat.formatToParts` against the chinese
// calendar is one of the costlier calls in the timeline header, the result is
// immutable per calendar day, and the same days are re-requested on every
// header rebuild. Keyed by epoch ms (header dates are local midnights, stable
// per day); bounded in practice by the project's timeline span.
const rokuyoCache = new Map<number, Rokuyo | null>();

export function rokuyo(date: Date): Rokuyo | null {
  const cacheKey = date.getTime();
  const cached = rokuyoCache.get(cacheKey);
  if (cached !== undefined || rokuyoCache.has(cacheKey)) return cached ?? null;
  const result = computeRokuyo(date);
  rokuyoCache.set(cacheKey, result);
  return result;
}

function computeRokuyo(date: Date): Rokuyo | null {
  const fmt = getLunarFormatter();
  if (!fmt) return null;
  const parts = fmt.formatToParts(date);
  const monthPart = parts.find((p) => p.type === 'month')?.value;
  const dayPart = parts.find((p) => p.type === 'day')?.value;
  if (!monthPart || !dayPart) return null;
  // Strip a possible leap-month marker (e.g. "5bis", "5L") — leap months use
  // the same canonical month number for rokuyō computation.
  const month = parseInt(monthPart, 10);
  const day = parseInt(dayPart, 10);
  if (!Number.isFinite(month) || !Number.isFinite(day)) return null;
  const index = (((month + day - 2) % 6) + 6) % 6;
  // `index` is mathematically constrained to 0–5, so the lookup always
  // resolves — the assertion is just for `noUncheckedIndexedAccess`.
  return ROKUYO_NAMES[index] ?? null;
}
